import React from 'react';
import { useNavigate } from 'react-router-dom';

function InstallAppGuide() {
  const navigate = useNavigate();
  const [deferredPrompt, setDeferredPrompt] = React.useState(null);
  const [isInstallable, setIsInstallable] = React.useState(false);

  React.useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) {
      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setDeferredPrompt(null);
      setIsInstallable(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white p-6">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <img 
            src="/logo-bkb.jpg" 
            alt="Bank Kreiz Breizh" 
            className="w-24 h-24 rounded-2xl mx-auto mb-4 shadow-2xl"
          />
          <h1 className="text-3xl font-bold mb-2">BKB Pay</h1>
          <p className="text-gray-300">Application de paiement sans contact</p>
        </div>

        {/* Install Button */}
        {isInstallable && (
          <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-2xl p-6 mb-8 shadow-2xl">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">📱 Installation disponible</h2>
            <p className="text-gray-800 mb-4">Installez BKB Pay sur votre téléphone pour un accès rapide</p>
            <button
              onClick={handleInstall}
              className="w-full py-4 bg-gray-900 text-white rounded-xl font-bold text-lg hover:bg-gray-800"
            >
              Installer l'application
            </button>
          </div>
        )}

        {/* Features */}
        <div className="bg-blue-900 rounded-2xl p-6 mb-6">
          <h2 className="text-2xl font-bold mb-4">✨ Fonctionnalités</h2>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <span className="text-2xl">📱</span>
              <div>
                <h3 className="font-bold">Paiement NFC rapide</h3>
                <p className="text-sm text-gray-300">Payez en quelques secondes avec QR code dynamique</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-2xl">💳</span>
              <div>
                <h3 className="font-bold">Multi-cartes</h3>
                <p className="text-sm text-gray-300">Gérez vos cartes Visa, Mastercard et CB</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-2xl">🔒</span>
              <div>
                <h3 className="font-bold">100% sécurisé</h3>
                <p className="text-sm text-gray-300">Vos données protégées par Bank Kreiz Breizh</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <span className="text-2xl">⚡</span>
              <div>
                <h3 className="font-bold">Hors ligne</h3>
                <p className="text-sm text-gray-300">Accès à vos cartes même sans connexion</p>
              </div>
            </div>
          </div>
        </div>

        {/* Installation Guide */}
        <div className="bg-slate-800 rounded-2xl p-6 mb-6">
          <h2 className="text-2xl font-bold mb-4">📲 Comment installer ?</h2>
          
          <div className="space-y-4">
            <div>
              <h3 className="font-bold mb-2 flex items-center gap-2">
                <span className="bg-blue-600 w-6 h-6 rounded-full flex items-center justify-center text-sm">📱</span>
                Sur iOS (iPhone/iPad)
              </h3>
              <ol className="list-decimal list-inside space-y-1 text-sm text-gray-300 ml-8">
                <li>Ouvrez cette page dans Safari</li>
                <li>Appuyez sur le bouton Partager <span className="text-blue-400">□↑</span></li>
                <li>Sélectionnez "Sur l'écran d'accueil"</li>
                <li>Appuyez sur "Ajouter"</li>
              </ol>
            </div>

            <div>
              <h3 className="font-bold mb-2 flex items-center gap-2">
                <span className="bg-green-600 w-6 h-6 rounded-full flex items-center justify-center text-sm">🤖</span>
                Sur Android
              </h3>
              <ol className="list-decimal list-inside space-y-1 text-sm text-gray-300 ml-8">
                <li>Ouvrez cette page dans Chrome</li>
                <li>Appuyez sur le menu (⋮) en haut à droite</li>
                <li>Sélectionnez "Installer l'application"</li>
                <li>Confirmez l'installation</li>
              </ol>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          <button
            onClick={() => navigate('/mobile-pay')}
            className="w-full py-4 bg-yellow-500 text-gray-900 rounded-xl font-bold text-lg hover:bg-yellow-400"
          >
            Ouvrir BKB Pay
          </button>
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full py-4 bg-slate-700 text-white rounded-xl font-semibold hover:bg-slate-600"
          >
            Retour au dashboard
          </button>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-400">
          <p>Bank Kreiz Breizh - Banque Centrale de Bretagne</p>
          <p className="mt-1">© 2025 - Tous droits réservés</p>
        </div>
      </div>
    </div>
  );
}

export default InstallAppGuide;
